import { useState, useEffect, useCallback } from 'react';
import { financialStatementApi } from '../services/financialStatementApi';
import type {
    FinancialStatementLine,
    FSFilterOptions,
    FSFilters,
    FSView,
    RenderedViewLine,
} from '../types/financialStatement';

/** Pick the best default view: company-specific default > global default > first available */
function pickDefault(views: FSView[], viewType: string, company: string): FSView | undefined {
    const ofType = views.filter(v => v.ViewType === viewType);
    // 1. Company-specific default
    const companyDefault = ofType.find(v => v.IsDefault && v.CompanyCode === company);
    if (companyDefault) return companyDefault;
    // 2. Global default (CompanyCode = null)
    const globalDefault = ofType.find(v => v.IsDefault && !v.CompanyCode);
    if (globalDefault) return globalDefault;
    // 3. First available
    return ofType[0];
}

export function useFinancialStatement() {
    const [lines, setLines] = useState<FinancialStatementLine[]>([]);
    const [renderedIncomeLines, setRenderedIncomeLines] = useState<RenderedViewLine[]>([]);
    const [renderedBalanceLines, setRenderedBalanceLines] = useState<RenderedViewLine[]>([]);
    const [filterOptions, setFilterOptions] = useState<FSFilterOptions>({ companies: [], years: [] });
    const [filters, setFilters] = useState<FSFilters>({
        company: 'dch',
        year: new Date().getFullYear() - 1,
        month: 12,
        comparisonYear: new Date().getFullYear() - 2,
        comparisonMonth: 12,
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // View state
    const [availableViews, setAvailableViews] = useState<FSView[]>([]);
    const [selectedIncomeViewId, setSelectedIncomeViewId] = useState<number | null>(null);
    const [selectedBalanceViewId, setSelectedBalanceViewId] = useState<number | null>(null);

    const incomeViews = availableViews.filter(v => v.ViewType === 'IncomeStatement');
    const balanceViews = availableViews.filter(v => v.ViewType === 'BalanceSheet');

    // Load filter options and views on mount
    useEffect(() => {
        Promise.all([
            financialStatementApi.getFilters(),
            financialStatementApi.getViews({ company: filters.company }),
        ]).then(([opts, viewResult]) => {
            setFilterOptions(opts);
            setAvailableViews(viewResult.views);

            // Auto-select default views (company-specific > global > first)
            const defaultIncome = pickDefault(viewResult.views, 'IncomeStatement', filters.company);
            const defaultBalance = pickDefault(viewResult.views, 'BalanceSheet', filters.company);
            if (defaultIncome) setSelectedIncomeViewId(defaultIncome.ViewId);
            if (defaultBalance) setSelectedBalanceViewId(defaultBalance.ViewId);

            // Set year to latest available
            if (opts.years.length > 0 && !opts.years.includes(filters.year)) {
                const latestYear = opts.years[0];
                setFilters(prev => ({
                    ...prev,
                    year: latestYear,
                    comparisonYear: latestYear - 1,
                }));
            }
        }).catch(err => setError(err.message));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Reload views when company changes
    useEffect(() => {
        financialStatementApi.getViews({ company: filters.company })
            .then(result => {
                setAvailableViews(result.views);
                // Re-select best default for new company
                const bestIncome = pickDefault(result.views, 'IncomeStatement', filters.company);
                const bestBalance = pickDefault(result.views, 'BalanceSheet', filters.company);
                setSelectedIncomeViewId(bestIncome?.ViewId ?? null);
                setSelectedBalanceViewId(bestBalance?.ViewId ?? null);
            })
            .catch(() => {/* ignore */});
    }, [filters.company]); // eslint-disable-line react-hooks/exhaustive-deps

    // Load statement data when filters or view selection changes
    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const fetchPromises: Promise<void>[] = [];

            // Income statement (view-driven)
            if (selectedIncomeViewId) {
                fetchPromises.push(
                    financialStatementApi.getStatement({
                        company: filters.company,
                        year: filters.year,
                        month: filters.month,
                        comparisonYear: filters.comparisonYear,
                        comparisonMonth: filters.comparisonMonth,
                        viewId: selectedIncomeViewId,
                    }).then(result => {
                        setRenderedIncomeLines(result.lines as RenderedViewLine[]);
                    })
                );
            }

            // Balance sheet (view-driven)
            if (selectedBalanceViewId) {
                fetchPromises.push(
                    financialStatementApi.getStatement({
                        company: filters.company,
                        year: filters.year,
                        month: filters.month,
                        comparisonYear: filters.comparisonYear,
                        comparisonMonth: filters.comparisonMonth,
                        viewId: selectedBalanceViewId,
                    }).then(result => {
                        setRenderedBalanceLines(result.lines as RenderedViewLine[]);
                    })
                );
            }

            // Also fetch legacy lines (for fallback and totals)
            fetchPromises.push(
                financialStatementApi.getStatement({
                    company: filters.company,
                    year: filters.year,
                    month: filters.month,
                    comparisonYear: filters.comparisonYear,
                    comparisonMonth: filters.comparisonMonth,
                }).then(result => {
                    setLines(result.lines as FinancialStatementLine[]);
                })
            );

            await Promise.all(fetchPromises);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load data');
        } finally {
            setLoading(false);
        }
    }, [filters, selectedIncomeViewId, selectedBalanceViewId]);

    useEffect(() => {
        if (filters.company && filters.year) {
            loadData();
        }
    }, [loadData]);

    const updateFilters = useCallback((updates: Partial<FSFilters>) => {
        setFilters(prev => ({ ...prev, ...updates }));
    }, []);

    const reloadViews = useCallback(async () => {
        const result = await financialStatementApi.getViews({ company: filters.company });
        setAvailableViews(result.views);
    }, [filters.company]);

    // Legacy computed values (from non-view data)
    const incomeStatementLines = lines.filter(l => l.LineType === 'ProfitLoss');
    const balanceSheetLines = lines.filter(l => l.LineType === 'Assets' || l.LineType === 'Liabilities');
    const assetLines = lines.filter(l => l.LineType === 'Assets');
    const liabilityLines = lines.filter(l => l.LineType === 'Liabilities');

    const totalAssets = assetLines
        .filter(l => !l.IsSubtotal)
        .reduce((sum, l) => sum + l.CurrentYearAmount, 0);
    const totalLiabilities = liabilityLines
        .filter(l => !l.IsSubtotal)
        .reduce((sum, l) => sum + l.CurrentYearAmount, 0);
    const rawDiff = Math.round((totalAssets - totalLiabilities) * 100) / 100;
    const balanceDifference = Math.abs(rawDiff) < 1 ? 0 : rawDiff;

    return {
        lines,
        incomeStatementLines,
        balanceSheetLines,
        assetLines,
        liabilityLines,
        totalAssets,
        totalLiabilities,
        balanceDifference,
        filterOptions,
        filters,
        updateFilters,
        loading,
        error,
        reload: loadData,
        // View-related
        availableViews,
        incomeViews,
        balanceViews,
        selectedIncomeViewId,
        selectedBalanceViewId,
        setSelectedIncomeViewId,
        setSelectedBalanceViewId,
        renderedIncomeLines,
        renderedBalanceLines,
        reloadViews,
    };
}
